/**
 * Auto-scroll speed levels and constants
 * These define the interval timing (in milliseconds) for auto-scrolling.
 * Lower values = faster scrolling.
 */
export const SPEED_LEVELS = [
  { speed: 150, label: '1' },
  { speed: 130, label: '2' },
  { speed: 110, label: '3' },
  { speed: 90, label: '4' },
  { speed: 70, label: '5' },
  { speed: 60, label: '6' },
  { speed: 50, label: '7' },
];

/**
 * Default scrolling speed (corresponds to speed level 4)
 */
export const DEFAULT_SCROLL_SPEED = 90;

/**
 * Constant-mode speed in px/s. The engine runs on requestAnimationFrame now, but a level still
 * means what it always did: one pixel every `speed` milliseconds.
 */
export const speedToPxPerSecond = (speed) => 1000 / speed;

/* ---------------------------------------------------------------------------
 * Smart Auto Scroll
 * ------------------------------------------------------------------------- */

/**
 * Where on screen the performer reads, as a fraction of the viewport height. A part should have
 * reached this line by the time it starts. Below the halfway mark, so there's still a line or two
 * of the previous part above it and a good stretch of what's coming below.
 */
export const READING_FRACTION = 0.45;

/**
 * How many bars a single lyrics line is assumed to take.
 *
 * Measured, not guessed. 54 parts across 7 songs were played to a metronome and timed at their
 * boundaries; dividing each part's real duration by its seconds-per-bar gives its true bar count,
 * and the median came to 1.98 bars per written lyrics line. It had been 1, which made every sung
 * part run at half the time it needs — the single largest error in the schedule.
 */
export const BARS_PER_LYRIC_LINE = 2;

/**
 * Bar counts a part is allowed to take, in bars.
 *
 * Music is written in phrases, and a phrase is almost always a power of two bars — occasionally a
 * multiple of three or twelve. In the same 76-part recording, 63 parts landed within 10% of 4, 8 or
 * 16 bars, and the mean distance from a whole bar was 0.18. So a line count that works out at 7
 * bars is not a 7-bar part; it is a 8-bar part counted imprecisely, and rounding to the nearest
 * plausible phrase is closer to the truth than the raw arithmetic. Snapping cut the median error
 * from 37% to 4%.
 */
export const PHRASE_LENGTHS = [2, 4, 8, 12, 16, 24, 32];

/**
 * A Note is an instruction to the player ("watch the cue", "acapella"), not something anyone sings.
 * It is read, not performed, so its length has nothing to do with how much text it holds: the six
 * notes in the recording all took about two bars whether they ran to one line or two, and whether
 * they were five characters or twenty-five.
 *
 * Held at four times that measured length on purpose, and the gap is the point. Two bars is how
 * long a note takes to pass, not how long it takes to read, and the two are not the same job: a
 * part scrolls at the pace the band plays it, but a note has to sit still long enough for someone
 * to look up, notice it, and act on it before the cue it is warning about arrives. Eight bars is
 * chosen for that, not fitted to the recording.
 *
 * It is a deliberately asymmetric bet. The cost of erring long is drift — the note is never
 * measured, so the pace multiplier is free to pull it back, and the schedule absorbs the rest. The
 * cost of erring short is the note scrolling by unread, which is the whole point of it missed. Do
 * not tune this back towards the measured two bars on the strength of the timings alone; they
 * measure the wrong thing.
 */
export const NOTE_BARS = 8;

/** Rounds a raw bar estimate to the nearest phrase length it plausibly is. */
export const snapToPhrase = (bars) => {
  if (!(bars > 0)) return 0;
  // Beyond the table the phrase idea stops paying — a very long part is a medley or a vamp, and
  // rounding it to 32 would be a worse guess than the count itself
  if (bars > PHRASE_LENGTHS[PHRASE_LENGTHS.length - 1]) return Math.round(bars);
  return PHRASE_LENGTHS.reduce((best, length) =>
    Math.abs(length - bars) < Math.abs(best - bars) ? length : best,
  );
};

/**
 * Instrumental parts (Intro/Music/Instrumental/Outro) often carry a line of chords and nothing
 * else. Bars are counted from the chords when there are any; this is the last resort.
 */
export const DEFAULT_INSTRUMENTAL_BARS = 4;

/** A chord line stands for at least this many bars, however few chords are written on it. */
export const MIN_BARS_PER_CHORD_LINE = 1;

/** Floor for a chords-only part, so a two-chord Intro doesn't fly past. */
export const MIN_INSTRUMENTAL_BARS = 2;

/** Nothing scrolls faster than this, whatever the estimate says. */
export const MAX_PX_PER_SEC = 200;

/**
 * A total outside this range means the estimate has gone wrong (a missing BPM, a part with a
 * runaway line count), so the schedule falls back to the default average velocity instead.
 */
export const MIN_PLAUSIBLE_SONG_SECONDS = 10;
export const MAX_PLAUSIBLE_SONG_SECONDS = 20 * 60;

/** Pace multiplier: the user's one calibration knob over the computed schedule. */
export const PACE_MIN = 0.5;
export const PACE_MAX = 2.0;
export const PACE_STEP = 0.05;
export const DEFAULT_PACE = 1;

export const clampPace = (pace) => Math.min(PACE_MAX, Math.max(PACE_MIN, Math.round(pace / PACE_STEP) * PACE_STEP));
